import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRightLeft, faSpinner } from '@/fontawesome.ts';
import Modal from '@/components/ui/Modal.tsx';
import { APIRoutes } from '@/services/api.routes.ts';
import { handleAPIError, showErrorToast, showSuccessToast } from '@/utils/toasts.ts';
import type { Course, Module, Video, CourseID, ModuleID } from '@/models';

interface MoveVideoModalProps {
  isOpen: boolean;
  onClose: () => void;
  video: Video | null;
  onVideoMoved: (video: Video) => void;
}

export default function MoveVideoModal({ isOpen, onClose, video, onVideoMoved }: MoveVideoModalProps) {
  const [courses, setCourses] = useState<Course[]>([]);
  const [modules, setModules] = useState<Module[]>([]);
  const [selectedCourseId, setSelectedCourseId] = useState<CourseID | ''>('');
  const [selectedModuleId, setSelectedModuleId] = useState<ModuleID | ''>('');
  const [loadingData, setLoadingData] = useState(false);
  const [moving, setMoving] = useState(false);

  useEffect(() => {
    if (!isOpen || video === null) {
      return;
    }

    const loadData = async () => {
      try {
        setLoadingData(true);
        const [coursesResponse, modulesResponse] = await Promise.all([
          APIRoutes.listCourses({ limit: 1000 }),
          APIRoutes.listAllModules({ limit: 1000 })
        ]);

        const sortedCourses = [...coursesResponse.courses].sort((a, b) => a.order - b.order);
        const sortedModules = [...modulesResponse.modules].sort((a, b) => a.order - b.order);

        setCourses(sortedCourses);
        setModules(sortedModules);
        setSelectedCourseId(video.courseID);
        setSelectedModuleId(video.moduleID);
      } catch (err) {
        console.error('Error loading courses/modules for move:', err);
        showErrorToast('Fehler beim Laden der Kurse und Module');
        handleAPIError(err);
      } finally {
        setLoadingData(false);
      }
    };

    loadData();
  }, [isOpen, video]);

  const modulesForSelectedCourse = modules
    .filter((module) => module.courseID === selectedCourseId);

  const handleCourseChange = (courseId: CourseID | '') => {
    setSelectedCourseId(courseId);
    // Preselect the first module of the newly selected course (if any)
    const firstModule = modules.find((module) => module.courseID === courseId);
    setSelectedModuleId(firstModule?.id ?? '');
  };

  const handleMove = async () => {
    if (video === null) {
      return;
    }
    if (selectedModuleId === '') {
      showErrorToast('Bitte wählen Sie ein Zielmodul aus');
      return;
    }

    try {
      setMoving(true);
      const updatedVideo = await APIRoutes.moveVideo(video.id, selectedModuleId);
      showSuccessToast('Video erfolgreich verschoben');
      onVideoMoved(updatedVideo);
      onClose();
    } catch (err) {
      console.error('Error moving video:', err);
      showErrorToast('Fehler beim Verschieben des Videos');
      handleAPIError(err);
    } finally {
      setMoving(false);
    }
  };

  const handleClose = () => {
    if (!moving) {
      onClose();
    }
  };

  const isSameModule = video !== null && selectedModuleId === video.moduleID;
  const canMove = selectedModuleId !== '' && !isSameModule && !moving && !loadingData;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Video verschieben"
      icon={<FontAwesomeIcon icon={faRightLeft} className="w-5 h-5 text-blue-600" />}
      closeDisabled={moving}
      maxWidth="lg"
    >
      <div className="p-6 space-y-6">
        {video !== null && (
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Video: <span className="font-medium text-gray-900 dark:text-white">{video.title}</span>
          </div>
        )}

        {loadingData ? (
          <div className="flex justify-center items-center py-8">
            <FontAwesomeIcon icon={faSpinner} className="w-6 h-6 text-blue-600 dark:text-blue-400 animate-spin" />
          </div>
        ) : (
          <>
            <div>
              <label htmlFor="move-course" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Zielkurs
              </label>
              <select
                id="move-course"
                value={selectedCourseId}
                onChange={(e) => handleCourseChange(e.target.value as CourseID)}
                disabled={moving}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-50 cursor-pointer"
              >
                {courses.map((course) => (
                  <option key={course.id} value={course.id}>
                    {course.title}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="move-module" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Zielmodul
              </label>
              <select
                id="move-module"
                value={selectedModuleId}
                onChange={(e) => setSelectedModuleId(e.target.value as ModuleID)}
                disabled={moving || modulesForSelectedCourse.length === 0}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-50 cursor-pointer"
              >
                {modulesForSelectedCourse.length === 0 && (
                  <option value="">Keine Module in diesem Kurs</option>
                )}
                {modulesForSelectedCourse.map((module) => (
                  <option key={module.id} value={module.id}>
                    {module.title}
                    {video !== null && module.id === video.moduleID ? ' (aktuelles Modul)' : ''}
                  </option>
                ))}
              </select>
            </div>

            {isSameModule && (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Das Video befindet sich bereits in diesem Modul. Bitte wählen Sie ein anderes Zielmodul.
              </p>
            )}
          </>
        )}

        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-600">
          <button
            type="button"
            onClick={handleClose}
            disabled={moving}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 cursor-pointer"
          >
            Abbrechen
          </button>
          <button
            type="button"
            onClick={handleMove}
            disabled={!canMove}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 cursor-pointer"
          >
            {moving ? (
              <>
                <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 mr-2 animate-spin" />
                Verschiebe...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faRightLeft} className="w-4 h-4 mr-2" />
                Verschieben
              </>
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
}
